import { PageTitle, useIntl } from "@metsooutotec/modes-react-components"
import { observer } from "mobx-react-lite"
import { FC, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { getOnHandSensors } from "../stock-management-helper"
import { SDASensor } from "features/iot/stock-management/models/sensor-dispatch-allocation"
import stockManagementStore from "features/iot/stock-management/stores/stock-management-store"
import ListView, { TemplateItem } from "components/utils/list-view/list-view"
import { convertDateToDDMMMYY } from "components/utils/date-formatter-utils"
import LowSpinner from "components/common/low-spinner"
import { NotFound } from "components/utils/not-found"
import { itemViewListContainerStyles } from "components/common-style/common-style"

interface tableSensorModel extends SDASensor {
    id: string
    customerName: string
    siteName: string
    allocatedDate: Date | null
}
const OnHandSensorsListView: FC = observer(() => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { partNumber } = useParams()
    const [listOfSensors, setListOfSensors] = useState<tableSensorModel[]>([])
    const allSensors = stockManagementStore.allSensorsStockManagement
    const title = `On Hand Sensors For ${formatMessage("sensorBatches.partNumber")}: ${partNumber}`

    useEffect(() => {
        if (!stockManagementStore.stockManagementLoading && partNumber && allSensors.length > 0) {
            const sensors = getOnHandSensors(partNumber)
            const listOfSensors: tableSensorModel[] = sensors.map(s => ({
                ...s,
                id: s.macAddress,
                customerName: stockManagementStore.getSensorCustomerNameByMacAddress(s.macAddress) || "",
                siteName: stockManagementStore.getSensorSiteNameByMacAddress(s.macAddress) || "",
                allocatedDate: s.allocationDate || null,
            }))
            setListOfSensors(listOfSensors)
        }
    }, [partNumber, stockManagementStore.stockManagementLoading, allSensors.length])

    const breadcrumbsPath = [
        {
            text: formatMessage("stockManagement.title"),
            to: "/stock-management",
        },
        {
            text: partNumber as string,
            to: `/stock-management/${partNumber}`,
        },
    ]

    const template: TemplateItem<tableSensorModel>[] = [
        {
            headingKey: "headings.serialNumber",
            cellFunc: (a: tableSensorModel) => a.serialNumber,
        },
        {
            headingKey: "headings.macAddress",
            cellFunc: (a: tableSensorModel) => a.macAddress,
        },
        {
            headingKey: "headings.customer",
            cellFunc: (a: tableSensorModel) => a.customerName,
        },
        {
            headingKey: "headings.site",
            cellFunc: (a: tableSensorModel) => a.siteName,
        },
        {
            headingKey: "headings.allocationDate",
            cellFunc: (a: tableSensorModel) => {
                return convertDateToDDMMMYY(a.allocatedDate)
            },
        },
    ]

    return allSensors.length < 1 ? (
        <LowSpinner id={"sensorBatch.loading"} />
    ) : !listOfSensors ? (
        <NotFound id={title} />
    ) : (
        <>
            <PageTitle breadcrumbsPath={breadcrumbsPath} onPathChange={navigate} title={title} />
            <div style={itemViewListContainerStyles}>
                <ListView data={listOfSensors || []} noneYetKey='sensor.notFound' template={template} />
            </div>
        </>
    )
})

export default OnHandSensorsListView
